/**
 * useCapabilityManifest — TanStack Query wrapper around
 * ``fetchCapabilityManifest()`` from ``@ando/core``.
 *
 * Single source of truth for the capability manifest in v2. Other hooks
 * in this slice (``useCapability``, ``useHasCapability``) read from this
 * query so cache hits cascade.
 *
 * Staleness: capabilities only change after OAuth callbacks (BE invalidates
 * its Redis cache + pushes ``capabilities_changed`` WS). 5 min staleTime is
 * generous; refetchOnWindowFocus catches OAuth callbacks that landed in a
 * sibling tab while this tab was backgrounded.
 */

import { queryOptions, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { fetchCapabilityManifest, type CapabilityManifest } from "@ando/core";

export const capabilityManifestQueryKey = ["capabilities", "manifest"] as const;

export type UseCapabilityManifestResult = UseQueryResult<CapabilityManifest>;

/**
 * Shared query definition for the capability manifest. Consumed by BOTH the
 * ``useCapabilityManifest`` hook and route loaders (``ensureQueryData``) so a
 * hub that gates on a capability (e.g. calendar) can resolve its gate from
 * cache on first paint instead of after a round-trip.
 *
 * ``refetchOnWindowFocus`` is a hook-runtime concern and stays on the hook.
 */
export function capabilityManifestQueryOptions() {
  return queryOptions({
    queryKey: capabilityManifestQueryKey,
    queryFn: fetchCapabilityManifest,
    staleTime: 5 * 60_000,
  });
}

export function useCapabilityManifest(): UseCapabilityManifestResult {
  return useQuery({
    ...capabilityManifestQueryOptions(),
    refetchOnWindowFocus: true,
    // Non-fatal: leave the last good manifest in place on a transient 5xx so
    // a blip doesn't blank out every banner across the app. Default retry=3.
  });
}

/** Configuration affordance only; no scope selection or query grant. */
export function isTramaLakehouseConfigured(
  manifest: CapabilityManifest | undefined,
  userId: string | null,
  failed: boolean,
  selectedConnectionIds: readonly string[],
  connectSpaceId: string | null,
  projectId: string | null,
): boolean {
  const hint = manifest?.trama_lakehouse;
  return !failed && userId !== null && manifest?.user_id === userId
    && hint?.configured === true && typeof hint.connection_id === "string"
    && hint.connection_id.length > 0 && connectSpaceId === null && projectId === null
    && (selectedConnectionIds.length === 0
      || (selectedConnectionIds.length === 1 && selectedConnectionIds[0] === hint.connection_id));
}
